/**
 * Officer submission hook — manages member roster + entry submission.
 * Sprint 9, SDD §7.4
 *
 * Provides:
 *   - Battalion member roster (GET /api/admin/members)
 *   - Submit entry for member (POST /api/admin/punch)
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import type { MemberOption } from '../components/MemberSelector';

export interface OfficerPunchPayload {
  member_id: number;
  category: string;
  subcategory?: string;
  position?: string;
  apparatus?: string;
  shift_type?: string;
  notes?: string;
  clock_in: string;
  clock_out: string;
}

interface AdminPunchResponse {
  id: number;
  member_id: number;
  category: string;
  position: string | null;
  apparatus: string | null;
  clock_in: string;
  clock_out: string;
  duration_minutes: number;
  shift_type: string;
  submitted_by: number;
  source: string;
}

interface MembersResponse {
  data: MemberOption[];
}

export interface UseOfficerSubmitReturn {
  members: MemberOption[];
  membersLoading: boolean;
  submitting: boolean;
  error: string | null;
  success: AdminPunchResponse | null;
  submitEntry: (payload: OfficerPunchPayload) => Promise<boolean>;
  clearError: () => void;
  clearSuccess: () => void;
}

export function useOfficerSubmit(): UseOfficerSubmitReturn {
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<AdminPunchResponse | null>(null);

  // Fetch battalion roster on mount
  useEffect(() => {
    async function fetchMembers() {
      setMembersLoading(true);
      try {
        const data = await apiFetch<MembersResponse>('/api/admin/members');
        setMembers(data.data);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Failed to load members');
        }
      } finally {
        setMembersLoading(false);
      }
    }
    fetchMembers();
  }, []);

  const submitEntry = useCallback(async (payload: OfficerPunchPayload): Promise<boolean> => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await apiFetch<AdminPunchResponse>('/api/admin/punch', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setSuccess(data);
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to submit entry. Please try again.');
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  return {
    members,
    membersLoading,
    submitting,
    error,
    success,
    submitEntry,
    clearError,
    clearSuccess,
  };
}
